import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { CollapsedPopover } from "@/components/doc/right-sidebar/CollapsedPopover";
import { GraphExpressions } from "@/components/doc/right-sidebar/graph-style/GraphExpressions";
import { GraphGridStyle } from "@/components/doc/right-sidebar/graph-style/GraphGridStyle";
import { GraphLayout } from "@/components/doc/right-sidebar/graph-style/GraphLayout";
import { addGraphExpression, useAxisVariables } from "@/components/graph/utils";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { ActionsDisclosure } from "@/components/sidebar/ActionsDisclosure";
import { isNodeEditable } from "@/components/tiptap/lock/utils";
import { GraphNodeModel } from "@/models/nodes/GraphNodeModel";
import { twMerge } from "@/utils/twMerge";

import { GraphAxisLabels } from "./GraphAxisLabels";
import { GraphAxisLabelsToggler } from "./GraphAxisLabelsToggler";

import type { DocumentModel } from "@/models/DocumentModel";

const Content = observer(({ doc }: { doc: DocumentModel }) => {
  const t = useTranslations("sidebar");
  const { graphAttrs } = doc.editor.editorState;
  const graphNode = graphAttrs ? doc.nodes.get(graphAttrs.graphId) : null;

  const graphExpressionNodes = graphNode?.is(GraphNodeModel) ? graphNode.graphExpressionNodes : [];
  const expressions = graphExpressionNodes.map((n) => n.inlineNode!.nodes);
  const { axisVariables } = useAxisVariables(expressions);

  if (!graphNode?.is(GraphNodeModel)) return null;

  const editable = isNodeEditable(graphNode);

  return (
    <>
      <div className="px-2.25">
        <GraphExpressions node={graphNode} />
        {graphAttrs && editable && (
          <>
            <Sidebar.Delimiter className="mt-2 mb-2.5 mx-[1px]" />
            <GraphLayout node={graphNode} graphAttrs={graphAttrs} />
            <Sidebar.Delimiter className="mt-2 mb-0 mx-[1px]" />
          </>
        )}
      </div>
      {graphAttrs && editable && (
        <ActionsDisclosure
          title={t("activeNode.graph.title.gridStyle")}
          aria-label={t("activeNode.graph.title.gridStyle")}
          className="w-full mt-0"
          buttonClassName="pt-0"
          disclosureProps={{ defaultOpen: true }}
          disclosurePanelProps={{ className: "px-[5px]" }}
        >
          <GraphGridStyle node={graphNode} graphAttrs={graphAttrs} />
          <GraphAxisLabelsToggler doc={doc} graphAttrs={graphAttrs} axisVariables={axisVariables} />
          {graphAttrs.axisLabels && (
            <>
              <Sidebar.Delimiter className="mt-2 mb-1 mx-0" />
              <GraphAxisLabels doc={doc} graphAttrs={graphAttrs} />
            </>
          )}
        </ActionsDisclosure>
      )}
    </>
  );
});

type Props = {
  doc: DocumentModel;
  isCollapsed?: boolean;
};

export const GraphStyle = observer(function GraphStyle({ doc, isCollapsed }: Props) {
  const t = useTranslations("sidebar");
  const { graphAttrs } = doc.editor.editorState;
  const graphNode = graphAttrs ? doc.nodes.get(graphAttrs.graphId) : null;

  if (!graphNode?.is(GraphNodeModel)) return null;

  const addExpression = () => addGraphExpression(graphNode);

  const actionButton = isNodeEditable(graphNode) && (
    <Sidebar.Button
      type="button"
      inverted
      title={t("activeNode.graph.actions.addExpression")}
      className={twMerge(
        "min-h-5.5 h-5.5 w-5.5 p-0 mr-[5px]",
        isCollapsed && "absolute right-0 top-1.5",
      )}
      icon={<Icon name="Plus" />}
      onClick={addExpression}
    />
  );

  if (isCollapsed) {
    return (
      <div className="flex flex-col gap-1 rounded-8 p-0.75 bg-gray-100 dark:bg-gray-900">
        <CollapsedPopover
          icon={<Icon name="Graph" />}
          tooltipTitle={t("blocks.graph.title")}
          title={t("blocks.graph.title")}
        >
          {actionButton}
          <Content doc={doc} />
        </CollapsedPopover>
      </div>
    );
  }

  return (
    <ActionsDisclosure
      data-panel="graph_style"
      title={t("blocks.graph.title")}
      aria-label={t("blocks.graph.title")}
      action={actionButton}
      className="w-full p-0"
      disclosureProps={{ defaultOpen: true }}
      disclosurePanelProps={{ className: "[&>div]:p-0 pb-[5px]" }}
    >
      <Content doc={doc} />
    </ActionsDisclosure>
  );
});
